import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { BottomNav } from '../components/BottomNav';
import { AppHeader } from '../components/AppHeader';
import { hasFeature } from '../utils/featureFlags';

interface Alert {
  id: string;
  alert_type: string;
  severity: string;
  message: string;
  created_at: string;
  resolved_at?: string;
  is_resolved: number;
  group_count?: number;   // 同種の未解決件数（サーバで集約した代表行に付与）
  group_ids?: string[];   // 同種の全ID（まとめて確認用）
  user_id?: string;       // 対象の乗組員（サーバが全アラートに付与）
  user_name?: string;
  vessel_name?: string;
}

/** ログイン中ユーザーのID（自分自身のアラートには名前を出さない判定に使う） */
function currentUserId(): string {
  try { return String((JSON.parse(localStorage.getItem('catms_user') ?? '{}') as { user_id?: string })?.user_id ?? ''); }
  catch { return ''; }
}

/** 対象の乗組員名バッジ（フラグ alert_target_names・自分以外のアラートのみ） */
const TargetName: React.FC<{ alert: Alert; myId: string; muted?: boolean }> = ({ alert: a, myId, muted }) => {
  if (!hasFeature('alert_target_names')) return null;
  if (!a.user_name || (a.user_id && a.user_id === myId)) return null;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 6, padding: '3px 10px', borderRadius: 20, background: muted ? '#f1f5f9' : 'rgba(255,255,255,0.75)', border: '1px solid ' + (muted ? '#e2e8f0' : 'rgba(15,23,42,0.08)') }}>
      <span style={{ fontSize: 11 }}>👤</span>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: muted ? '#64748b' : '#0f172a' }}>
        {a.user_name}{a.vessel_name ? <span style={{ fontWeight: 600, color: '#64748b' }}>（{a.vessel_name}）</span> : null}
      </span>
    </div>
  );
};

const ALERT_CONFIG: Record<string, { label: string; icon: string; bg: string; color: string; border: string }> = {
  no_clock_out:      { label: '退勤打刻なし',    icon: '🚪', bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  overtime_exceeded: { label: '残業時間超過',    icon: '⏰', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  consecutive_work:  { label: '連続勤務日数超過', icon: '📆', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  alcohol_detected:  { label: 'アルコール検知',  icon: '🧪', bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  rest_violation:    { label: '休息時間不足',    icon: '😴', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  missing_clock_in:  { label: '出勤打刻なし',    icon: '⏱', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  license_expiring:      { label: '海技免状の期限警告', icon: '📄', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  seaman_book_expiring:  { label: '船員手帳の期限警告', icon: '📔', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  certificate_expiring:  { label: '資格・証明書の期限',   icon: '📜', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  sync_delay:            { label: '同期遅延',           icon: '🔄', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  device_vessel_mismatch:{ label: '船舶不一致ログイン', icon: '🚢', bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  alcohol_skipped:       { label: 'アルコール検査未実施', icon: '🧪', bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  alcohol_manual:        { label: 'アルコール手入力',     icon: '✍️', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  alcohol_device_limit:  { label: '検知器の交換時期',     icon: '🧪', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  alcohol_device_milestone: { label: '検知器の使用回数',  icon: '🧪', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  face_missing:          { label: '顔写真なし打刻',       icon: '📷', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
};

// 未定義タイプでも英語の内部名をそのまま出さない（日本語の汎用ラベルに落とす）
const FALLBACK_ACTIVE   = { label: 'その他の警告', icon: '⚠️', bg: '#fffbeb', color: '#d97706', border: '#fde68a' };
const FALLBACK_RESOLVED = { label: 'その他の警告', icon: '⚠️', bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' };

function fmtDate(iso: string): string {
  try {
    // サーバのcreated_atは 'YYYY-MM-DD HH:MM:SS'(UTC)。タイムゾーン無しはUTCとして解釈する
    const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(iso);
    const d = new Date(iso.replace(' ', 'T') + (hasZone ? '' : 'Z'));
    return d.toLocaleString('ja-JP', {
      month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Tokyo', hour12: false,
    });
  } catch { return iso.slice(0, 16); }
}

export const AlertsPage: React.FC = () => {
  const [active,   setActive]   = useState<Alert[]>([]);
  const [resolved, setResolved] = useState<Alert[]>([]);
  const myId = currentUserId();
  const [loading,  setLoading]  = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, resolvedRes] = await Promise.all([
        api.get<any>('/api/alerts?is_resolved=0'),
        api.get<any>('/api/alerts?is_resolved=1&this_month=1'),
      ]);
      setActive(activeRes?.data ?? []);
      setResolved(resolvedRes?.data ?? []);
    } catch {
      setActive([]);
      setResolved([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleConfirm = async (a: Alert) => {
    try {
      const ids = a.group_ids && a.group_ids.length > 0 ? a.group_ids : [a.id];
      if (ids.length > 1) {
        // 同種の未解決をまとめて解決（確認後に裏の重複が繰り上がるのを防ぐ）
        await api.put('/api/alerts/resolve-bulk', { ids });
      } else {
        await api.post(`/api/alerts/${a.id}/dismiss`, {});
      }
      await fetchAlerts();
    } catch { /* ignore */ }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      {/* ヘッダー */}
      <AppHeader
        title="アラート"
        right={active.length > 0 ? (
          <div style={{ background: '#ef4444', color: 'white', borderRadius: 20, padding: '4px 11px', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
            未確認 {active.length}件
          </div>
        ) : undefined}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>読み込み中...</div>
        ) : active.length === 0 && resolved.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
            <span style={{ fontSize: 56 }}>👍</span>
            <p style={{ color: '#0f172a', fontSize: 16, fontWeight: 700, margin: 0 }}>アラートはありません</p>
          </div>
        ) : (
          <>
            {/* 未確認アラート */}
            {active.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14 }}>
                <span style={{ fontSize: 22 }}>✅</span>
                <span style={{ fontSize: 14, color: '#166534', fontWeight: 600 }}>未確認のアラートはありません</span>
              </div>
            ) : (
              active.map(a => {
                const cfg = ALERT_CONFIG[a.alert_type] ?? FALLBACK_ACTIVE;
                return (
                  <div key={a.id} style={{ background: cfg.bg, border: '1px solid ' + cfg.border, borderRadius: 16, padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <span style={{ fontSize: 28 }}>{cfg.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: cfg.color }}>{cfg.label}</span>
                          {(a.group_count ?? 1) > 1 && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: cfg.color, color: 'white', whiteSpace: 'nowrap' }}>
                              同種 {a.group_count}件
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtDate(a.created_at)}</div>
                      </div>
                      {a.severity === 'critical' && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', whiteSpace: 'nowrap' }}>重要</span>
                      )}
                    </div>
                    <TargetName alert={a} myId={myId} />
                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: '0 0 12px' }}>{a.message}</p>
                    <button onClick={() => handleConfirm(a)}
                      style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid ' + cfg.border, background: 'white', color: cfg.color, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {(a.group_count ?? 1) > 1 ? `${a.group_count}件まとめて確認済みにする` : '確認済みにする'}
                    </button>
                  </div>
                );
              })
            )}

            {/* 今月の確認済みアラート */}
            {resolved.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <button onClick={() => setShowResolved(p => !p)}
                  style={{ width: '100%', padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: 12, background: 'white', color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>今月の確認済みアラート</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: '#e2e8f0', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700, color: '#475569' }}>{resolved.length}件</span>
                    <span style={{ fontSize: 12 }}>{showResolved ? '▲' : '▼'}</span>
                  </span>
                </button>
                {showResolved && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {resolved.map(a => {
                      const cfg = ALERT_CONFIG[a.alert_type] ?? FALLBACK_RESOLVED;
                      return (
                        <div key={a.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px', opacity: 0.8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <span style={{ fontSize: 22 }}>{cfg.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#475569' }}>{cfg.label}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>発生: {fmtDate(a.created_at)}</div>
                              {a.resolved_at && (
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>確認: {fmtDate(a.resolved_at)}</div>
                              )}
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: '#dcfce7', color: '#166534', whiteSpace: 'nowrap' }}>確認済</span>
                          </div>
                          <TargetName alert={a} myId={myId} muted />
                          <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, margin: 0 }}>{a.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};
